import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { 
  Shield, CheckCircle, TrendingUp, AlertTriangle,
  Clock, Target, ChevronRight, Info, Zap, Star,
  FileCheck, Eye, MessageSquare, Calendar
} from 'lucide-react';
import AEDIcon from './icons/AEDIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Buyer Transaction Profile Component
 * 
 * Shows BSS (Buyer Seriousness Score) with:
 * - Tier display (not big number first)
 * - What's strengthening your profile
 * - What's limiting your tier
 * - Improvement guidance
 * 
 * Tone: Affirmation, not grading. Gold for growth, no punishment language.
 */
const BuyerTransactionProfile = ({ token, buyerId }) => {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (token && buyerId) {
      fetchScore();
    }
  }, [token, buyerId]);

  const fetchScore = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/buyer/my-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setScoreData(data.data);
      } else if (data.data) {
        setScoreData(data.data);
      }
    } catch (err) {
      console.error('Error fetching BSS:', err);
    }
    setLoading(false);
  };

  const getTierConfig = (tier) => {
    const configs = {
      'VERIFIED_PRIME': {
        label: 'Verified Prime',
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-600/10 border-emerald-600/30',
        gradientClass: 'from-emerald-600/20 to-emerald-600/5',
        description: 'Highest reliability tier - fastest transactions, lowest commitments',
        commitmentRange: '5-7%'
      },
      'SERIOUS': {
        label: 'Serious Buyer',
        color: 'text-blue-400',
        bgColor: 'bg-blue-600/10 border-blue-600/30',
        gradientClass: 'from-blue-600/20 to-blue-600/5',
        description: 'Strong transaction profile - priority access to properties',
        commitmentRange: '8-10%'
      },
      'EXPLORATORY': {
        label: 'Exploratory',
        color: 'text-amber-400',
        bgColor: 'bg-amber-600/10 border-amber-600/30',
        gradientClass: 'from-amber-600/20 to-amber-600/5',
        description: 'Building your transaction history',
        commitmentRange: '10-12%'
      },
      'NON_PRIORITY': {
        label: 'Getting Started',
        color: 'text-gray-400',
        bgColor: 'bg-gray-600/10 border-gray-600/30',
        gradientClass: 'from-gray-600/20 to-gray-600/5',
        description: 'Complete key actions to improve your profile',
        commitmentRange: '12-15%'
      },
      'UNSCORED': {
        label: 'New Profile',
        color: 'text-gray-400',
        bgColor: 'bg-gray-600/10 border-gray-600/30',
        gradientClass: 'from-gray-600/20 to-gray-600/5',
        description: 'Complete actions to build your transaction profile',
        commitmentRange: '10%'
      }
    };
    return configs[tier] || configs['UNSCORED'];
  };

  // Parse strengths from score data
  const getStrengths = () => {
    if (!scoreData) return [];
    const strengths = [];
    
    // Check for positive signals
    if (scoreData.kyc_verified || scoreData.kycStatus === 'approved') {
      strengths.push({ icon: FileCheck, text: 'KYC approved on first submission', weight: 'high' });
    }
    if (scoreData.commitment_held || scoreData.hasCommitment) {
      strengths.push({ icon: Shield, text: 'Commitment held successfully', weight: 'high' });
    }
    if (scoreData.no_show_count === 0 || scoreData.noShowRate === 0) {
      strengths.push({ icon: Calendar, text: '0 missed viewings', weight: 'medium' });
    }
    if (scoreData.offer_count > 0) {
      strengths.push({ icon: AEDIcon, text: `${scoreData.offer_count || 1}+ offers submitted`, weight: 'medium' });
    }
    if (scoreData.response_time_fast || scoreData.avgResponseTime < 24) {
      strengths.push({ icon: MessageSquare, text: 'Responds quickly during negotiations', weight: 'low' });
    }
    if (scoreData.deals_completed > 0) {
      strengths.push({ icon: CheckCircle, text: `${scoreData.deals_completed} deal(s) completed`, weight: 'high' });
    }
    
    // If no specific strengths found, add default based on tier
    if (strengths.length === 0) {
      if (scoreData.tier === 'VERIFIED_PRIME' || scoreData.tier === 'SERIOUS') {
        strengths.push({ icon: Star, text: 'Consistent transaction behavior', weight: 'medium' });
      }
    }
    
    return strengths;
  };

  // Parse improvement areas from score data
  const getImprovementAreas = () => {
    if (!scoreData) return [];
    const areas = [];
    
    // Check for improvement signals (neutral language only)
    if (scoreData.kyc_rejected || scoreData.kycStatus === 'rejected') {
      areas.push({ 
        text: 'KYC verification pending',
        action: 'Complete KYC to unlock full platform access',
        priority: 'high'
      });
    }
    if (scoreData.no_show_count >= 2 || scoreData.noShowRate > 0.1) {
      areas.push({ 
        text: `${scoreData.no_show_count || '2+'} viewing no-shows in last 30 days`,
        action: 'Confirm or cancel viewings in advance',
        priority: 'medium'
      });
    }
    if (scoreData.withdrawal_count >= 2 || scoreData.withdrawalRate > 0.2) {
      areas.push({ 
        text: `${scoreData.withdrawal_count || '2+'} offers withdrawn in last 30 days`,
        action: 'Submit offers when ready to commit',
        priority: 'medium'
      });
    }
    if (scoreData.avg_response_hours > 48) {
      areas.push({ 
        text: 'Response time slower than platform average',
        action: 'Respond to counteroffers within 24 hours',
        priority: 'low'
      });
    }
    if (!scoreData.commitment_held && !scoreData.hasCommitment) {
      areas.push({ 
        text: 'No commitment on record',
        action: 'Hold commitment to demonstrate seriousness',
        priority: 'medium'
      });
    }
    
    return areas;
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-6 text-center">
          <Shield className="w-8 h-8 animate-pulse mx-auto mb-4 text-amber-400" />
          <p className="text-muted-foreground">Loading your transaction profile...</p>
        </CardContent>
      </Card>
    );
  }

  if (!scoreData) {
    return null;
  }

  // Handle INSUFFICIENT_DATA status
  if (scoreData.status === 'INSUFFICIENT_DATA') {
    return (
      <Card className="glass-card border border-white/10" data-testid="buyer-transaction-profile">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            Your Transaction Profile
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Your profile reflects how reliably you move from interest to completion
          </p>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg bg-amber-600/10 border border-amber-600/20 text-center">
            <Star className="w-8 h-8 mx-auto text-amber-400 mb-3" />
            <h3 className="text-lg font-display text-amber-400">Building Your Profile</h3>
            <p className="text-sm text-muted-foreground mt-2">
              {scoreData.message || 'Complete actions to build your transaction profile'}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Complete KYC, request viewings, or submit offers to get started
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const tier = scoreData.tier || 'UNSCORED';
  const tierConfig = getTierConfig(tier);
  const score = scoreData.score || 50;
  const strengths = getStrengths();
  const improvements = getImprovementAreas();

  return (
    <Card className={`glass-card border ${tierConfig.bgColor}`} data-testid="buyer-transaction-profile">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className={`w-5 h-5 ${tierConfig.color}`} />
              Your Transaction Profile
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Your profile reflects how reliably you move from interest to completion
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setExpanded(!expanded)}
            className="text-xs"
          >
            {expanded ? 'Less' : 'Details'}
            <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Tier Display - Name First, Not Number */}
        <div className={`p-4 rounded-lg bg-gradient-to-r ${tierConfig.gradientClass}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-xl font-display ${tierConfig.color}`}>
                {tierConfig.label}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Score: {score} / 100
              </p>
            </div>
            <div className="text-right">
              <Badge variant="outline" className={tierConfig.bgColor}>
                {tierConfig.commitmentRange} commitment
              </Badge>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress 
              value={score} 
              className="h-2 bg-black/20"
              indicatorClassName={`${tierConfig.color.replace('text-', 'bg-')}`}
            />
          </div>
          
          <p className="text-xs text-muted-foreground mt-2">
            {tierConfig.description}
          </p>
        </div>

        {/* What's Strengthening Your Profile */}
        {strengths.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              What's Strengthening Your Profile
            </h4>
            <div className="space-y-1.5">
              {strengths.map((s, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  {s.text}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What's Limiting Your Tier - Expanded View */}
        {expanded && improvements.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-white/10">
            <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Improvement Opportunities
            </h4>
            <div className="space-y-2">
              {improvements.map((imp, idx) => (
                <div key={idx} className="p-2 rounded bg-secondary/10">
                  <p className="text-sm text-muted-foreground">{imp.text}</p>
                  <p className="text-xs text-amber-400/80 mt-1 flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {imp.action}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Upgrade Path */}
            <div className="p-3 rounded bg-primary/10 border border-primary/20 mt-3">
              <p className="text-xs text-primary flex items-center gap-2">
                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                Improve consistency for 14 days to unlock the next tier.
              </p>
            </div>
          </div>
        )}

        {/* Commitment Impact - Only if not prime tier */}
        {expanded && tier !== 'VERIFIED_PRIME' && (
          <div className="p-3 rounded bg-secondary/10 border border-white/5">
            <p className="text-xs font-medium text-foreground">Risk-Adjusted Commitment</p>
            <p className="text-xs text-muted-foreground mt-1">
              Based on your current transaction profile, your recommended commitment is{' '}
              <span className={tierConfig.color}>{tierConfig.commitmentRange}</span>.
            </p>
            <p className="text-xs text-amber-400/80 mt-2">
              Improve your profile to reduce this requirement.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BuyerTransactionProfile;
